import { motion } from 'framer-motion';
import { Check, Zap, Shield, GitBranch } from 'lucide-react';

const bullets = [
  {
    icon: Zap,
    title: 'Real-time optimization',
    description:
      'Every campaign auto-tunes against live performance — bids, audiences, creatives, all without you lifting a finger.',
  },
  {
    icon: Shield,
    title: 'Brand-safe by default',
    description:
      'Tone, voice, and visual guardrails learned once from your brand book and enforced across every asset.',
  },
  {
    icon: GitBranch,
    title: 'Built for teams',
    description:
      'Approval flows, comments, version history, and role-based access — the kind of rigor agencies actually need.',
  },
];

const checklist = [
  'Multi-channel publishing (IG, FB, TikTok, LinkedIn, X, YouTube)',
  'AI-generated captions, hashtags, and creative variants',
  'Automated A/B testing across audiences and copy',
  'Native integrations with HubSpot, Klaviyo, Shopify, GA4',
  'White-label client reporting in one click',
  'SOC2-ready data handling and SSO',
];

export default function Features() {
  return (
    <section className="relative bg-background text-foreground py-24 md:py-32 px-8 md:px-28">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <div className="liquid-glass inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-6">
            <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              The platform
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-medium tracking-[-1.5px] leading-[1.05] mb-5">
            One operating system for every{' '}
            <span className="font-serif italic font-normal">
              channel you run.
            </span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-lg">
            SprynAI replaces seven SaaS tabs with one intelligent workspace.
            Strategy, content, ads, and reporting — connected end to end.
          </p>

          <ul className="mt-8 space-y-3">
            {checklist.map((item, i) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: 0.05 * i }}
                className="flex items-start gap-3 text-sm md:text-base text-foreground/85"
              >
                <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-white/[0.04]">
                  <Check className="h-3 w-3 text-foreground" />
                </span>
                {item}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <div className="grid grid-cols-1 gap-4">
          {bullets.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="rounded-2xl border border-border bg-card p-7 hover:bg-white/[0.04] hover:border-white/15 transition-colors transform-gpu"
              >
                <div className="liquid-glass inline-flex items-center justify-center h-11 w-11 rounded-xl mb-5">
                  <Icon className="h-5 w-5 text-foreground" />
                </div>
                <h3 className="text-xl font-semibold tracking-tight mb-2">
                  {b.title}
                </h3>
                <p className="text-sm leading-6 text-muted-foreground">
                  {b.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
